import { partial } from 'lodash';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Button } from '@vimily/bonjoro-ui-components-v2/Button';
import {
  MeatballMenu,
  MeatballMenuItemProps,
} from '@vimily/bonjoro-ui-components-v2/MeatballMenu';

import { Modal } from '@vimily/bonjoro-ui-components-v2/Modal';
import { Greet, UserSimple } from '~/types/models';
import TextContainer from './Shared/TextContainer';
import { getProfileName } from '~/BonjorosList/util';
import updateGreetProfileAction from '~/actions/greets/updateGreetProfileAction';
import assignGreetsAction from '~/actions/greets/assignGreetsAction';
import assignWorkspaceGreetsAction from '~/actions/greets/assignWorkspaceGreetsAction';
import { first } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faUserPlus,
  faFolderOpen,
} from '@fortawesome/free-solid-svg-icons';
import { useDeleteGreets } from '~/react-hooks/use-delete-greets';
import { TeamMemberSelector } from '~/App/Components/Filters/TeamMemberSelector';
import WorkspaceDropdown from '~/App/Modules/Workspaces/Components/WorkspaceSelectDropdown';
import Workspace from '~/App/Modules/Workspaces/Types/Workspace';
import { queryKeys } from '~/App/Api/Greets';

export interface ProfileHeaderProps {
  greet: Greet;
  className?: string;
}

interface GreetProfileFormData {
  email: string;
  firstName: string;
  lastName: string;
  note: string;
}

type UpdateGreetProfileMutation = GreetProfileFormData & {
  greetId: string;
  profileId?: string;
};

function ProfileHeader({ greet, className }: ProfileHeaderProps) {
  const { profiles } = greet;
  const profile = first(profiles);

  const [editModalIsOpen, setEditModalOpen] = useState(false);
  const [assignModalIsOpen, setAssignModalOpen] = useState(false);
  const [workspaceModalIsOpen, setWorkspaceModalOpen] = useState(false);
  const { register, handleSubmit } = useForm<GreetProfileFormData>();
  const queryClient = useQueryClient();
  const { handleDeleteClick } = useDeleteGreets([greet]);

  const updateGreetProfileFn = async ({
    greetId,
    profileId,
    email,
    firstName,
    lastName,
    note,
  }: UpdateGreetProfileMutation): Promise<Greet> => {
    const res = (await updateGreetProfileAction({
      greetId,
      profileId,
      email,
      firstName,
      lastName,
      note,
    })) as { data: { greets: Greet } };

    return res.data.greets;
  };

  const { mutateAsync: updateGreetProfile, isLoading: loading } = useMutation(
    updateGreetProfileFn,
    {
      onSuccess: (data: Greet, { greetId }) => {
        queryClient.setQueryData(['greet', { id: greetId }], data);
      },
    }
  );

  const { mutateAsync: assignGreets } = useMutation(assignGreetsAction, {
    onSuccess: () => {
      queryClient.invalidateQueries('todos');
      queryClient.invalidateQueries(queryKeys.all);
      setAssignModalOpen(false);
    },
  });

  const { mutateAsync: assignWorkspaceGreets } = useMutation(
    assignWorkspaceGreetsAction,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('todos');
        queryClient.invalidateQueries(queryKeys.all);
        setWorkspaceModalOpen(false);
      },
    }
  );

  const onSubmit: SubmitHandler<GreetProfileFormData> = async (
    formData,
    event
  ) => {
    event?.preventDefault();

    await updateGreetProfile({
      greetId: greet.id,
      profileId: profile?.id,
      ...formData,
    });

    setEditModalOpen(false);
  };

  const onCancelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    setEditModalOpen(false);
  };

  const handleChangeAssignee = async (assignee?: UserSimple) => {
    await assignGreets({
      greetIds: [greet.id],
      assigneeId: assignee?.id ?? undefined,
    });
  };

  const handleChangeWorkspace = async (workspace: Workspace | null) => {
    if (!workspace) return;
    await assignWorkspaceGreets({
      greetIds: [greet.id],
      campaignId: workspace.id,
    });
  };

  const profileMeatballMenuItems: MeatballMenuItemProps[] = [
    {
      logo: <i className='icon icon-sm icon-edit-contact' />,
      label: 'Edit contact',
      onClick: partial(setEditModalOpen, true),
    },
    {
      logo: <FontAwesomeIcon icon={faUserPlus} className='!w-4 !h-4' />,
      label: 'Assign',
      onClick: partial(setAssignModalOpen, true),
    },
    {
      logo: <FontAwesomeIcon icon={faFolderOpen} className='!w-4 !h-4' />,
      label: 'Move',
      onClick: partial(setWorkspaceModalOpen, true),
    },
    {
      logo: <FontAwesomeIcon icon={faTrash} className='!w-4 !h-4' />,
      label: 'Dismiss',
      className: 'v2-text-red-700',
      onClick: handleDeleteClick,
    },
  ];

  return (
    <div className={className}>
      <TextContainer>
        <div className='p-0 m-0 flex items-center'>
          {profile && (
            <div className='flex flex-col'>
              <span className='text-[21px] text-neutral-900 font-bold'>
                {getProfileName(profile)}
              </span>
              {profile.position && (
                <span className='text-xs text-neutral-500 font-semibold'>
                  {profile.position}
                </span>
              )}
            </div>
          )}
          <MeatballMenu
            bodyClassName='ml-5'
            items={profileMeatballMenuItems}
            align='start'
          />

          {/* Edit Contact Modal */}
          <Modal
            open={editModalIsOpen}
            onModalClose={partial(setEditModalOpen, false)}
            className='!max-w-lg !p-8'
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='form-group'>
                <label>Email Address</label>
                <input
                  className='form-control'
                  type='email'
                  name='email'
                  defaultValue={profile?.email}
                  ref={register}
                />
              </div>
              <div className='form-group'>
                <label>First Name</label>
                <input
                  className='form-control'
                  name='firstName'
                  defaultValue={profile?.first_name ?? ''}
                  ref={register}
                />
              </div>
              <div className='form-group'>
                <label>Last Name</label>
                <input
                  className='form-control'
                  name='lastName'
                  defaultValue={profile?.last_name ?? ''}
                  ref={register}
                />
              </div>
              <div className='form-group'>
                <label>Task Identifier</label>
                <input
                  className='form-control'
                  name='note'
                  defaultValue={greet.event?.message ?? ''}
                  ref={register}
                />
              </div>

              <Button className='border-solid' disabled={loading}>
                {!loading && 'Save'}
                {loading && (
                  <>
                    {'Saving '}
                    <i className='far fa-spinner fa-spin' />
                  </>
                )}
              </Button>
              <Button
                variant='info'
                className='border-solid ml-2'
                onClick={onCancelClick}
              >
                Cancel
              </Button>
            </form>
          </Modal>

          {/* Assign Modal */}
          <Modal
            open={assignModalIsOpen}
            onModalClose={partial(setAssignModalOpen, false)}
            className='!w-auto !max-w-sm !p-3'
          >
            <div className='p-4'>
              <h3 className='text-base font-semibold mb-3'>Assign Task</h3>
              <div className='mb-3'>
                <TeamMemberSelector
                  bodyPlaceholder='Select team member'
                  selectedUser={greet.assignee}
                  onSelect={handleChangeAssignee}
                  hideAllSelector
                  showPlaceholderAsSelected={false}
                  width={250}
                />
              </div>
              <div className='flex gap-2 justify-end'>
                <Button
                  variant='info'
                  onClick={() => setAssignModalOpen(false)}
                  className='px-3 py-1.5 text-sm'
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Modal>

          {/* Workspace Modal */}
          <Modal
            open={workspaceModalIsOpen}
            onModalClose={partial(setWorkspaceModalOpen, false)}
            className='!w-auto !max-w-sm !p-3'
          >
            <div className='p-4'>
              <h3 className='text-base font-semibold mb-3'>
                Move to Workspace
              </h3>
              <div className='mb-3'>
                <WorkspaceDropdown
                  onWorkspaceChange={handleChangeWorkspace}
                  selectedWorkspace={greet.campaign}
                  label='Select workspace'
                  hideAllOption
                  showLabelAsSelected={false}
                  width={250}
                />
              </div>
              <div className='flex gap-2 justify-end'>
                <Button
                  variant='info'
                  onClick={() => setWorkspaceModalOpen(false)}
                  className='px-3 py-1.5 text-sm'
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      </TextContainer>
    </div>
  );
}

export default ProfileHeader;
